import { useContext, useEffect, useState } from 'react';
import {
  ListSubheader,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Divider
} from '@mui/material';
import {
  Dashboard,
  Drafts,
  AppRegistration,
  ViewList,
  CancelPresentation
} from '@mui/icons-material';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import SuperAdminNavList from './SuperAdminNavList';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PetsIcon from '@mui/icons-material/Pets';
import ReportIcon from '@mui/icons-material/Report';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  canAccessInspectionSchedule,
  canAccessPermitCloseOut,
  canAccessReports
} from 'src/utils/permitFeatureAccess';
const MenuWrapper = styled(Box)(({ theme }) => ({
  '.MuiList-root': {
    padding: theme.spacing(1),
    '& > .MuiList-root': {
      padding: 0
    }
  },
  '.MuiListSubheader-root': {
    textTransform: 'uppercase',
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.common.white,
    padding: theme.spacing(0, 2.5),
    lineHeight: 1.4
  }
}));

const SubMenuWrapper = styled(Box)(({ theme }) => ({
  '.MuiList-root': {
    '.MuiListItem-root': {
      padding: theme.spacing(1, 0),
      '.MuiBadge-root': {
        position: 'absolute',
        right: theme.spacing(3.2),
        '.MuiBadge-standard': {
          background: theme.palette.primary.main,
          fontSize: theme.typography.pxToRem(10),
          fontWeight: 'bold',
          textTransform: 'uppercase',
          color: theme.palette.primary.contrastText
        }
      },
      '.MuiButton-root': {
        display: 'flex',
        color: theme.palette.text.secondary,
        backgroundColor: 'transparent',
        width: '100%',
        justifyContent: 'flex-start',
        padding: theme.spacing(1.2, 3),
        '.MuiButton-startIcon, .MuiButton-endIcon': {
          transition: theme.transitions.create(['color'])
        },
        '&.active, &:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.common.white,
          '.MuiButton-startIcon, .MuiButton-endIcon': {
            color: theme.palette.common.white
          }
        },
        '.MuiButton-startIcon': {
          color: theme.palette.text.disabled,
          fontSize: theme.typography.pxToRem(20),
          marginRight: theme.spacing(1)
        },
        '.MuiButton-endIcon': {
          color: theme.palette.text.primary,
          opacity: 0.8,
          fontSize: theme.typography.pxToRem(20)
        }
      }
    }
  }
}));

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);

  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );
  const showNavItems = useSelector(
    (store: RootState) => store.navigation.showNavItems
  );
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const location = useLocation();
  const isAdmin = userDetails.UserRole === 'Admin';
  const [activePath, setActivePath] = useState(
    decodeURIComponent(location.pathname)
  );

  useEffect(() => {
    setActivePath(decodeURIComponent(location.pathname));
  }, [location.pathname]);

  const handleButtonClick = (path: string) => {
    setActivePath(path);
    closeSidebar();
  };

  const isAuthorized = canAccessReports(userDetails, currentPermitDetails?.id);

  const isSuperAdmin = userDetails.UserRole === 'Super Admin';
  const applicationNavItems = [
    {
      label: 'New Application',
      path: 'new-application',
      icon: <AppRegistration />
    },
    {
      label: 'All Applications',
      path: 'application-list',
      icon: <ViewList />
    },
    {
      label: 'Draft Applications',
      path: 'draft-application',
      icon: <Drafts />
    },

    ...(canAccessPermitCloseOut(userDetails, currentPermitDetails?.id)
      ? [
          {
            label: 'Permit Close Out',
            path: 'close-out',
            icon: <CancelPresentation />
          }
        ]
      : []),
    ...(currentPermitDetails?.id === 10
      ? [
          {
            label: userDetails?.UserRole == 'User' ? 'My Pets' : 'Pets',
            path: 'my-pets',
            icon: <PetsIcon />
          }
        ]
      : []),
    ...(canAccessInspectionSchedule(userDetails, currentPermitDetails?.id)
      ? [
          {
            label: 'Inspection Schedule',
            path: 'inspection-schedule',
            icon: <ScheduleIcon />
          }
        ]
      : []),
    ...(isAuthorized
      ? [
          {
            label: 'Reports',
            path: 'report',
            icon: <ReportIcon />
          }
        ]
      : [])
    // (
    //   (isAdmin && currentPermitDetails?.id === 10) ||
    //   (isAdmin && isManager && currentPermitDetails?.id === 9)
    // ) && {
    //   label: 'Reports',
    //   path: 'report',
    //   icon: <ReportIcon />
    // }
    // (
    //   (isAdmin && currentPermitDetails?.id === 1) ||
    //   (isAdmin && currentPermitDetails?.id === 2) ||
    //   (isAdmin && currentPermitDetails?.id === 4) ||
    //   (isAdmin && currentPermitDetails?.id === 5) ||
    //   (isAdmin && currentPermitDetails?.id === 6) ||
    //   (isAdmin && currentPermitDetails?.id === 7) ||
    //   (isAdmin && currentPermitDetails?.id === 8) ||
    //   (isAdmin && isManager && currentPermitDetails?.id === 9) ||
    //   (isAdmin && currentPermitDetails?.id === 10)
    // ) && {
    //   label: 'Reports',
    //   path: 'report',
    //   icon: <ReportIcon />
    // }
  ];

  const onClickFunction = () => {
    closeSidebar();
  };

  return (
    <MenuWrapper className="sidebar_menu_wrapper">
      <List sx={{ pl: { xs: '0 !important', sm: 'auto' } }}>
        <SubMenuWrapper>
          <List
            component="div"
            className="menu-list"
            sx={{ pl: { xs: '0 !important', sm: 'auto' } }}
          >
            <ListItem>
              <Button
                disableRipple
                component={RouterLink}
                onClick={() =>
                  handleButtonClick(`/dashboard/${userDetails?.TownshipID}`)
                }
                to={`/dashboard/${userDetails?.TownshipID}`}
                className={`menu-link ${
                  activePath === `/dashboard/${userDetails?.TownshipID}`
                    ? 'active'
                    : ''
                }`}
                startIcon={<Dashboard />}
              >
                Dashboard
              </Button>
            </ListItem>
            {isAdmin && (
              <ListItem>
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={() => handleButtonClick(`/subscriptions`)}
                  to={`/subscriptions`}
                  className={`menu-link ${
                    activePath === `/subscriptions` ? 'active' : ''
                  }`}
                  startIcon={<RocketLaunchIcon />}
                >
                  Subscriptions
                </Button>
              </ListItem>
            )}
          </List>
        </SubMenuWrapper>
      </List>

      {(isSuperAdmin || showNavItems) && (
        <List
          sx={{ pl: { xs: '0 !important', sm: 'auto' } }}
          component="div"
          subheader={
            <ListSubheader
              component="div"
              disableSticky
              className="menu-subheader"
              style={{ color: '#223354' }}
            >
              {'Actions'}
            </ListSubheader>
          }
        >
          {isSuperAdmin && (
            <SubMenuWrapper>
              <SuperAdminNavList />
            </SubMenuWrapper>
          )}
          {!isSuperAdmin && showNavItems && (
            <SubMenuWrapper>
              <List
                component="div"
                className="menu-list"
                sx={{ pl: { xs: '0 !important', sm: 'auto' } }}
              >
                {applicationNavItems.map((item, index) => (
                  <>
                    <ListItem key={index}>
                      <Button
                        disableRipple
                        component={RouterLink}
                        to={`/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}/${item.path}`}
                        className={`menu-link ${
                          activePath ===
                          `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}/${item.path}`
                            ? 'active'
                            : ''
                        }`}
                        onClick={onClickFunction}
                        startIcon={item.icon}
                        //onClickFunction(`/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}/${item.path}`
                      >
                        {item.label}
                      </Button>
                    </ListItem>
                    {/* {index < applicationNavItems.length - 1 && <Divider />} */}
                    <Divider />
                  </>
                ))}
              </List>
            </SubMenuWrapper>
          )}
        </List>
      )}
    </MenuWrapper>
  );
}

export default SidebarMenu;
